import type { Feature, Page } from '../feature';
import { esc, q, qa } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { currentUser } from '../../shared/session/session';
import { href, pagesFor } from '../router';

/** Menu lateral do backoffice: um item por página com `nav`, filtrado pelo papel de quem entrou. */
export function createSidebar(pages: Page[]): Feature & { setActive(pageId: string): void } {
  return {
    template: () => `
<div class="side-backdrop" id="side-backdrop" aria-hidden="true"></div>
<aside class="sidebar" id="sidebar">
  <div class="side-brand">
    <div class="brand-mark" aria-hidden="true"></div>
    <div class="brand-txt"><b>Rueda de la Fortuna</b><span>Backoffice</span></div>
    <button class="side-close" id="side-close" aria-label="Cerrar menú">✕</button>
  </div>
  <nav id="side-nav" aria-label="Menú"></nav>
  <div class="side-foot">Apueste Rápido</div>
</aside>`,

    mount() {
      /* celular: o menu é uma gaveta que abre pelo hambúrguer da barra superior */
      const setOpen = (open: boolean): void => {
        document.body.classList.toggle('nav-open', open);
        document.querySelector('#nav-toggle')?.setAttribute('aria-expanded', String(open));
      };
      document.addEventListener('click', e => {
        const el = e.target as Element;
        if (el.closest('#nav-toggle')) { setOpen(!document.body.classList.contains('nav-open')); return; }
        if (el.closest('#side-close') || el.closest('#side-backdrop') || el.closest('#side-nav .side-link')) setOpen(false);
      });
      document.addEventListener('keydown', e => { if (e.key === 'Escape') setOpen(false); });
      window.addEventListener('hashchange', () => setOpen(false));
      window.addEventListener('resize', () => { if (window.innerWidth > 900) setOpen(false); });
    },

    render() {
      const u = currentUser();
      const items = u ? pagesFor(pages, u.role).filter(p => p.nav) : [];
      const children = (parentId: string): Page[] => items.filter(p => p.nav!.parent === parentId);
      /* grupos na ordem em que aparecem; itens sem grupo ficam soltos no topo; subitens ficam sob a página-mãe */
      const groups = new Map<string, Page[]>();
      for (const p of items.filter(p => !p.nav!.parent)) {
        const g = p.nav!.group ?? '';
        if (!groups.has(g)) groups.set(g, []);
        groups.get(g)!.push(p);
      }
      const link = (p: Page, sub = false): string =>
        `<a class="side-link${sub ? ' side-sub' : ''}" href="${href(p.pattern)}" data-page="${p.id}" title="${esc(t(p.nav!.key))}"><i>${p.nav!.icon}</i><span>${esc(t(p.nav!.key))}</span></a>`;
      const item = (p: Page): string => {
        const subs = children(p.id);
        return subs.length
          ? `<div class="side-item" data-parent="${p.id}">${link(p)}<div class="side-subs">${subs.map(s => link(s, true)).join('')}</div></div>`
          : link(p);
      };
      q('#side-nav').innerHTML = [...groups.entries()].map(([g, ps]) =>
        (g ? `<div class="side-group">${esc(t('navgrp.' + g))}</div>` : '') + ps.map(item).join('')
      ).join('');
    },

    setActive(pageId: string) {
      const page = pages.find(p => p.id === pageId);
      const target = page?.navParent ?? pageId;
      const parent = pages.find(p => p.id === target)?.nav?.parent;
      qa('#side-nav .side-link').forEach(a => a.classList.toggle('on', a.dataset.page === target));
      /* a página-mãe fica marcada (mais suave) quando um subitem está ativo */
      qa<HTMLElement>('#side-nav .side-item').forEach(d => d.classList.toggle('has-on', d.dataset.parent === (parent ?? target)));
    }
  };
}
